import type {
  T_OnboardingHeardAboutSource,
  T_OnboardingIngestChoice,
  T_OnboardingV2SourceSelection,
} from "@lattice/core-lib/types/onboarding/onboarding.types";
import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, fromPromise, setup } from "xstate";

import {
  getOnboardingV2Greeting,
  ONBOARDING_V2_COPY,
} from "./onboarding-v2-flow";
import {
  getOnboardingV2MessageStreamDuration,
  ONBOARDING_V2_STREAM_TIMING,
} from "./onboarding-v2-stream";

export type T_OnboardingV2MachineInput = {
  recordIngestChoice: (choice: T_OnboardingIngestChoice) => Promise<void>;
  recordSource: (source: T_OnboardingV2SourceSelection) => Promise<void>;
};

export type T_OnboardingV2Context = {
  detailChoice: T_OnboardingIngestChoice | null;
  detailLabel: string | null;
  input: T_OnboardingV2MachineInput;
  progressError: string | null;
  progressErrorCode: string | null;
  progressFailureCount: number;
  sourceLabel: string | null;
  sourceSelection: T_OnboardingV2SourceSelection | null;
};

type T_NamedOnboardingSource = Exclude<T_OnboardingHeardAboutSource, "OTHER">;

export type T_OnboardingV2Event =
  | {
      label: string;
      type: "SELECT_SOURCE";
      value: T_NamedOnboardingSource;
    }
  | { type: "OPEN_SOURCE_TEXT" }
  | { type: "CANCEL_SOURCE_TEXT" }
  | { label: string; type: "SUBMIT_SOURCE_TEXT" }
  | {
      choice: T_OnboardingIngestChoice;
      label: string;
      type: "SELECT_DETAIL";
    }
  | { type: "RETRY_PROGRESS_SAVE" }
  | { type: "CHANGE_SOURCE" }
  | { type: "CHANGE_DETAIL" }
  | { type: "HANDOFF_CANCELLED" }
  | { type: "RESET" };

export const E_OnboardingV2State = {
  Welcome: "welcome",
  Greeting: "greeting",
  Prompt: "prompt",
  Source: "source",
  SourceOther: "sourceOther",
  SavingSource: "savingSource",
  SourceSaveFailed: "sourceSaveFailed",
  Typing: "typing",
  DetailPrompt: "detailPrompt",
  Detail: "detail",
  SavingDetail: "savingDetail",
  DetailSaveFailed: "detailSaveFailed",
  Handoff: "handoff",
} as const;

export type T_OnboardingV2State =
  (typeof E_OnboardingV2State)[keyof typeof E_OnboardingV2State];

type T_RecordSourceActorInput = {
  recordSource: T_OnboardingV2MachineInput["recordSource"];
  source: T_OnboardingV2SourceSelection | null;
};

type T_RecordIngestChoiceActorInput = {
  choice: T_OnboardingIngestChoice | null;
  recordIngestChoice: T_OnboardingV2MachineInput["recordIngestChoice"];
};

const onboardingV2SetupTypes: SetupTypes<
  T_OnboardingV2Context,
  T_OnboardingV2Event,
  Record<never, string>,
  string,
  T_OnboardingV2MachineInput,
  unknown,
  EventObject,
  MetaObject
> = {};

function getErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Your onboarding progress could not be saved. Please try again.";
}

export function getOnboardingV2ProgressErrorCode(
  error: unknown,
): string | null {
  if (typeof error !== "object" || error === null || !("data" in error)) {
    return null;
  }
  const data = error.data;
  if (typeof data !== "object" || data === null || !("code" in data)) {
    return null;
  }
  return typeof data.code === "string" ? data.code : null;
}

export const onboardingV2Machine = setup({
  types: onboardingV2SetupTypes,
  delays: {
    detailPromptStream: getOnboardingV2MessageStreamDuration([
      ONBOARDING_V2_COPY.detailContext,
      ONBOARDING_V2_COPY.detailQuestion,
    ]),
    detailTyping: 700,
    // Reserve one personalized-name token so options cannot unlock before a
    // named greeting's final word has settled.
    greetingStream: getOnboardingV2MessageStreamDuration([
      getOnboardingV2Greeting("Investor"),
    ]),
    initialTyping: ONBOARDING_V2_STREAM_TIMING.initialTypingMs,
    sourcePromptStream: getOnboardingV2MessageStreamDuration([
      ONBOARDING_V2_COPY.sourceIntroduction,
      ONBOARDING_V2_COPY.sourceQuestion,
    ]),
  },
  actors: {
    recordSource: fromPromise<undefined, T_RecordSourceActorInput>(
      async ({ input }) => {
        if (input.source === null) {
          throw new Error("Choose where you heard about Lattice first.");
        }
        await input.recordSource(input.source);
        return undefined;
      },
    ),
    recordIngestChoice: fromPromise<undefined, T_RecordIngestChoiceActorInput>(
      async ({ input }) => {
        if (input.choice === null) {
          throw new Error("Choose how to add your holdings first.");
        }
        await input.recordIngestChoice(input.choice);
        return undefined;
      },
    ),
  },
  actions: {
    applyDetail: assign({
      detailChoice: ({ event }) =>
        event.type === "SELECT_DETAIL" ? event.choice : null,
      detailLabel: ({ event }) =>
        event.type === "SELECT_DETAIL" ? event.label : null,
      progressError: null,
      progressErrorCode: null,
    }),
    applySource: assign({
      progressError: null,
      progressErrorCode: null,
      sourceLabel: ({ event }) => {
        if (
          event.type === "SELECT_SOURCE" ||
          event.type === "SUBMIT_SOURCE_TEXT"
        ) {
          return event.label;
        }
        return null;
      },
      sourceSelection: ({ event }) => {
        if (event.type === "SELECT_SOURCE") {
          return { value: event.value };
        }
        if (event.type === "SUBMIT_SOURCE_TEXT") {
          return { other_text: event.label.trim(), value: "OTHER" };
        }
        return null;
      },
    }),
    clearAll: assign({
      detailChoice: null,
      detailLabel: null,
      progressError: null,
      progressErrorCode: null,
      progressFailureCount: 0,
      sourceLabel: null,
      sourceSelection: null,
    }),
    clearDetail: assign({
      detailChoice: null,
      detailLabel: null,
      progressError: null,
      progressErrorCode: null,
    }),
    clearProgressError: assign({
      progressError: null,
      progressErrorCode: null,
    }),
    clearSource: assign({
      progressError: null,
      progressErrorCode: null,
      sourceLabel: null,
      sourceSelection: null,
    }),
    setProgressError: assign({
      progressError: (_, params: { error: unknown }) =>
        getErrorMessage(params.error),
      progressErrorCode: (_, params: { error: unknown }) =>
        getOnboardingV2ProgressErrorCode(params.error),
      progressFailureCount: ({ context }) => context.progressFailureCount + 1,
    }),
  },
}).createMachine({
  id: "onboardingV2",
  context: ({ input }) => ({
    detailChoice: null,
    detailLabel: null,
    input,
    progressError: null,
    progressErrorCode: null,
    progressFailureCount: 0,
    sourceLabel: null,
    sourceSelection: null,
  }),
  initial: E_OnboardingV2State.Welcome,
  on: {
    RESET: {
      actions: "clearAll",
      target: `.${E_OnboardingV2State.Welcome}`,
    },
  },
  states: {
    [E_OnboardingV2State.Welcome]: {
      after: {
        initialTyping: { target: E_OnboardingV2State.Greeting },
      },
    },
    [E_OnboardingV2State.Greeting]: {
      after: {
        greetingStream: { target: E_OnboardingV2State.Prompt },
      },
    },
    [E_OnboardingV2State.Prompt]: {
      after: {
        sourcePromptStream: { target: E_OnboardingV2State.Source },
      },
    },
    [E_OnboardingV2State.Source]: {
      on: {
        OPEN_SOURCE_TEXT: {
          target: E_OnboardingV2State.SourceOther,
        },
        SELECT_SOURCE: {
          actions: "applySource",
          target: E_OnboardingV2State.SavingSource,
        },
      },
    },
    [E_OnboardingV2State.SourceOther]: {
      on: {
        CANCEL_SOURCE_TEXT: {
          target: E_OnboardingV2State.Source,
        },
        SUBMIT_SOURCE_TEXT: {
          actions: "applySource",
          target: E_OnboardingV2State.SavingSource,
        },
      },
    },
    [E_OnboardingV2State.SavingSource]: {
      invoke: {
        src: "recordSource",
        input: ({ context }) => ({
          recordSource: context.input.recordSource,
          source: context.sourceSelection,
        }),
        onDone: {
          actions: "clearProgressError",
          target: E_OnboardingV2State.Typing,
        },
        onError: {
          actions: {
            type: "setProgressError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: E_OnboardingV2State.SourceSaveFailed,
        },
      },
    },
    [E_OnboardingV2State.SourceSaveFailed]: {
      on: {
        CHANGE_SOURCE: {
          actions: "clearSource",
          target: E_OnboardingV2State.Source,
        },
        RETRY_PROGRESS_SAVE: {
          actions: "clearProgressError",
          target: E_OnboardingV2State.SavingSource,
        },
      },
    },
    [E_OnboardingV2State.Typing]: {
      after: {
        detailTyping: { target: E_OnboardingV2State.DetailPrompt },
      },
    },
    [E_OnboardingV2State.DetailPrompt]: {
      after: {
        detailPromptStream: { target: E_OnboardingV2State.Detail },
      },
    },
    [E_OnboardingV2State.Detail]: {
      on: {
        SELECT_DETAIL: {
          actions: "applyDetail",
          target: E_OnboardingV2State.SavingDetail,
        },
      },
    },
    [E_OnboardingV2State.SavingDetail]: {
      invoke: {
        src: "recordIngestChoice",
        input: ({ context }) => ({
          choice: context.detailChoice,
          recordIngestChoice: context.input.recordIngestChoice,
        }),
        onDone: {
          actions: "clearProgressError",
          target: E_OnboardingV2State.Handoff,
        },
        onError: {
          actions: {
            type: "setProgressError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: E_OnboardingV2State.DetailSaveFailed,
        },
      },
    },
    [E_OnboardingV2State.DetailSaveFailed]: {
      on: {
        CHANGE_DETAIL: {
          actions: "clearDetail",
          target: E_OnboardingV2State.Detail,
        },
        RETRY_PROGRESS_SAVE: {
          actions: "clearProgressError",
          target: E_OnboardingV2State.SavingDetail,
        },
      },
    },
    [E_OnboardingV2State.Handoff]: {
      on: {
        HANDOFF_CANCELLED: {
          actions: "clearDetail",
          target: E_OnboardingV2State.Detail,
        },
      },
    },
  },
});
